import React from 'react';
import PropTypes from 'prop-types';
import { getSizedImageUrl } from '@shopify/theme-images';
import { formatMoney } from '@shopify/theme-currency';

function getImageUrl(image) {
  let imageSize = '400x';
  if (image.aspect_ratio < 1) {
    imageSize = 'x400';
  }
  return getSizedImageUrl(image.url, imageSize);
}

function SearchResultsContainer({ type, title, children }) {
  return (
    <div className={`search-modal__${type}`}>
      <div className='search-header section-header container--fluid'>
        <h3 className='search-modal__title'>{title}</h3>
      </div>
      <div className='search-modal__results container'>{children}</div>
    </div>
  );
}

function ProductResults({ products }) {
  return (
    <React.Fragment>
      <div className='collection__wrapper m-b-lg'>
        <div className='collection__content'>
          {products.map((product) => (
            <div key={product.id} className='collection__block'>
              <div className='search-result product-card product-card--center'>
                {product.featured_image.url && (
                  <div className='product-card__image'>
                    <a href={product.url}>
                      <img
                        src={getImageUrl(product.featured_image)}
                        alt={product.featured_image.alt}
                        className='search-result__image'
                      />
                    </a>
                  </div>
                )}
                <div className='search-result__meta product-card__detail'>
                  <div className='product-card__flex-wrapper'>
                    <h4 className='search-result__title product-card__title'>
                      <a href={product.url}>{product.title}</a>
                    </h4>
                    <div className='product-card__price'>
                      <span className='money'>
                        {formatMoney(product.price, window.theme.moneyFormat)}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </React.Fragment>
  );
}

function PageResults({ pages }) {
  return (
    <React.Fragment>
      <div className='container--fluid'>
        {pages.map((page) => (
          <div key={page.id} className='search-result search-result__page'>
            <h4 className='h3 search-result__title'>
              <a href={page.url}>{page.title}</a>
            </h4>
            <p className='small article-card__link text-center'>
              <a href={page.url} className='btn--text'>
                More
              </a>
            </p>
          </div>
        ))}
      </div>
    </React.Fragment>
  );
}

function ArticleResults({ articles }) {
  return (
    <React.Fragment>
      <div className='blog-grid'>
        {articles.map((article) => (
          <div key={article.id} className='article__block'>
            <div className='search-result article-card'>
              {article.featured_image.url && (
                <a href={article.url}>
                  <div className='article-card__image'>
                    <img
                      src={getImageUrl(article.featured_image)}
                      alt={article.featured_image.alt}
                      className='search-result__image'
                    />
                  </div>
                </a>
              )}
              <div className='article-card__details'>
                <div>
                  <div className='article-card__title'>
                    <h4 className='search-result__title h3'>
                      <a href={article.url}>{article.title}</a>
                    </h4>
                  </div>
                </div>
                <p className='small '>
                  <a
                    href={article.url}
                    className='article-card__link btn--text'
                  >
                    Read More
                  </a>
                </p>
              </div>
            </div>
          </div>
        ))}
      </div>
    </React.Fragment>
  );
}

function SearchResults({ results, searchTerm }) {
  const { products, pages, articles } = results;
  const rootUrl =
    window.theme.locale && window.theme.locale.primary
      ? ''
      : window.theme.locale.rootUrl;
  const colWidth =
    12 /
    (Number(products.length > 0) +
      Number(pages.length > 0) +
      Number(articles.length > 0));
  const totalResults = products.length + pages.length + articles.length;
  if (totalResults === 0) {
    return (
      <div className='container search-modal__results-container text-center'>
        <p>
          Sorry, no results for <span className='searchTerm'>{searchTerm}</span>
        </p>
      </div>
    );
  }
  return (
    <div className='search-modal__results-container'>
      {products.length > 0 && (
        <SearchResultsContainer
          colWidth={colWidth}
          type='products'
          title='Products'
        >
          <ProductResults products={products} />
        </SearchResultsContainer>
      )}
      {articles.length > 0 && (
        <SearchResultsContainer
          colWidth={colWidth}
          type='articles'
          title='Articles'
        >
          <ArticleResults articles={articles} />
        </SearchResultsContainer>
      )}
      {pages.length > 0 && (
        <SearchResultsContainer colWidth={colWidth} type='pages' title='Pages'>
          <PageResults pages={pages} />
        </SearchResultsContainer>
      )}
      <p className='text-center'>
        <a className='btn btn--text' href={`${rootUrl}/search?q=${searchTerm}`}>
          View more results for <span className='searchTerm'>{searchTerm}</span>
        </a>
      </p>
    </div>
  );
}

SearchResultsContainer.propTypes = {
  title: PropTypes.string,
  type: PropTypes.string,
  children: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.node),
    PropTypes.node,
  ]).isRequired,
  colWidth: PropTypes.number,
};

SearchResultsContainer.defaultProps = {
  colWidth: 4,
};

ProductResults.propTypes = {
  products: PropTypes.array,
};

PageResults.propTypes = {
  pages: PropTypes.array,
};

ArticleResults.propTypes = {
  articles: PropTypes.array,
};

SearchResults.propTypes = {
  results: PropTypes.object,
  searchTerm: PropTypes.string,
};

SearchResults.defaultProps = {
  results: {
    products: [],
    pages: [],
    articles: [],
  },
  searchTerm: '',
};

export default SearchResults;
